import type { ComputedRef } from 'vue'
import type { AppTableSelectOption } from '@shared/ui/selects'
import type { ChangelogCursorPaginationProps } from '@entities/changelog'

import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

interface UseChangelogCursorPaginationOptions {
  props: ChangelogCursorPaginationProps
  onPrev: () => void
  onNext: () => void
  onPerPageChange: (value: number) => void
}

interface UseChangelogCursorPaginationReturn {
  perPageOptions: AppTableSelectOption[]
  title: ComputedRef<string>
  isPrevDisabled: ComputedRef<boolean>
  isNextDisabled: ComputedRef<boolean>
  handlePrev: () => void
  handleNext: () => void
  handlePerPageChange: (value: number) => void
}

export const useChangelogCursorPagination = ({
  props,
  onPrev,
  onNext,
  onPerPageChange,
}: UseChangelogCursorPaginationOptions): UseChangelogCursorPaginationReturn => {
  const perPageOptions: AppTableSelectOption[] = ROWS_PER_PAGE_OPTIONS.map(value => ({
    value,
    text: String(value),
  }))

  const title = computed<string>(() =>
    `Showing ${props.count} ${props.count === 1 ? 'result' : 'results'}`,
  )

  const isPrevDisabled = computed<boolean>(() => !!props.disabled || !props.hasPrev)
  const isNextDisabled = computed<boolean>(() => !!props.disabled || !props.hasNext)

  const handlePrev = (): void => {
    if (isPrevDisabled.value) {
      return
    }

    onPrev()
  }

  const handleNext = (): void => {
    if (isNextDisabled.value) {
      return
    }

    onNext()
  }

  const handlePerPageChange = (value: number): void => {
    if (value === props.perPage) {
      return
    }

    onPerPageChange(value)
  }

  return {
    perPageOptions,
    title,
    isPrevDisabled,
    isNextDisabled,
    handlePrev,
    handleNext,
    handlePerPageChange,
  }
}
